import React, { useCallback, useEffect, useRef, useState } from 'react';
import { TelegramStatusEnum } from '../../enums/telegram';
import { NotificationEmailTypeEnum } from '../../enums/NotificationEmailTypeEnum';
import { FeatureTypeEnum } from '../../enums/FeatureTypeEnum';
import {
  Box,
  Typography,
  Button,
  Alert,
  Snackbar,
  Chip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { Notifications as NotificationsIcon } from '@mui/icons-material';
import MainLayout from '../../../../components/Layout/MainLayout';
import ContactManager, { ContactData } from '../../../../components/ContactManager/ContactManager';
import ApiConnectionAlert from '../../../../components/ApiConnectionAlert/ApiConnectionAlert';
import { useTranslation } from 'react-i18next';
import httpClient from '../../../../utils/httpClient';

const Notifications: React.FC = () => {
  const { t } = useTranslation();
  const initialSnapshotRef = useRef<string | null>(null);
  const [isDirty, setIsDirty] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingNav, setPendingNav] = useState<(() => void) | null>(null);
  const [globalContacts, setGlobalContacts] = useState<ContactData>({
    emails: [],
    telegrams: [],
    whatsapps: []
  });

  const [featureNotifications, setFeatureNotifications] = useState<Array<{
    featureType: FeatureTypeEnum;
    notificationEmailType: NotificationEmailTypeEnum;
    customEmails: Array<{ email: string; name?: string; role?: string }>;
  }>>([]);

  const [allowedSectors, setAllowedSectors] = useState<number[] | undefined>(undefined);
  const [telegramAlertMessage, setTelegramAlertMessage] = useState<string | undefined>(undefined);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error'
  });

  const makeSnapshot = (contacts: ContactData) => {
    const normalizeText = (v: unknown) => String(v ?? '').trim();
    const normalizeEmail = (v: unknown) => normalizeText(v).toLowerCase();
    const normalizeNumber = (v: unknown) => normalizeText(v);
    const normalizeSector = (v: unknown) => Number(v ?? 0);

    const emails = (contacts.emails || [])
      .filter(e => !!normalizeEmail((e as any)?.email))
      .map(e => ({
        email: normalizeEmail((e as any)?.email),
        name: normalizeText((e as any)?.name),
        role: normalizeText((e as any)?.role),
        sector: normalizeSector((e as any)?.sector)
      }))
      .sort((a, b) => a.email.localeCompare(b.email));

    const telegrams = (contacts.telegrams || [])
      .filter(tg => !!normalizeNumber((tg as any)?.number))
      .map(tg => ({
        number: normalizeNumber((tg as any)?.number),
        name: normalizeText((tg as any)?.name),
        role: normalizeText((tg as any)?.role),
        sector: normalizeSector((tg as any)?.sector)
      }))
      .sort((a, b) => a.number.localeCompare(b.number));

    const whatsapps = (contacts.whatsapps || [])
      .filter(w => !!normalizeNumber((w as any)?.number))
      .map(w => ({
        number: normalizeNumber((w as any)?.number),
        name: normalizeText((w as any)?.name),
        role: normalizeText((w as any)?.role),
        sector: normalizeSector((w as any)?.sector)
      }))
      .sort((a, b) => a.number.localeCompare(b.number));

    return JSON.stringify({ emails, telegrams, whatsapps });
  };

  const saveGlobalContacts = async (): Promise<boolean> => {
    try {
      // Map emails and telegrams from ContactManager to required API schema
      const payload = {
        addressEmailGlobal: (globalContacts.emails || []).map((e) => ({
          name: e.name || '',
          sector: Number(e.sector || 0),
          role: e.role || '',
          email: e.email || ''
        })),
        addressTelegramGlobal: (globalContacts.telegrams || []).map((t) => ({
          number: t.number || '',
          name: t.name || '',
          sector: Number(t.sector || 0),
          role: t.role || ''
        }))
      };

      const res = await httpClient.put('/api/v1/portal/customer/update-global-address', payload);

      // API may return a plain boolean or a wrapped success payload
      let ok = false;
      const raw = res?.data as any;
      if (typeof raw === 'boolean') {
        ok = raw === true;
      } else if (raw && typeof raw === 'object') {
        if (typeof raw.success === 'boolean' && raw.success === true) {
          // If success flag is present, consider operation succeeded
          ok = true;
        } else if (typeof raw.data === 'boolean') {
          ok = raw.data === true;
        }
      }

      setSnackbar({
        open: true,
        message: ok ? t('notifications_snackbar_success') : 'Falha ao salvar configurações globais.',
        severity: ok ? 'success' : 'error'
      });

      if (ok) {
        initialSnapshotRef.current = makeSnapshot(globalContacts);
        setIsDirty(false);
      }

      return ok;
    } catch (err: any) {
      const apiMsg = err?.response?.data?.errors?.[0]?.errorMessage;
      setSnackbar({
        open: true,
        message: apiMsg || 'Falha ao salvar configurações globais.',
        severity: 'error'
      });
      return false;
    }
  };

  const handleSave = async () => {
    await saveGlobalContacts();
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  const attemptNavigate = useCallback(
    (action: () => void) => {
      if (isDirty) {
        setPendingNav(() => action);
        setConfirmOpen(true);
      } else {
        action();
      }
    },
    [isDirty]
  );

  useEffect(() => {
    (window as any).__wgAccountSettingsGuard = (action: () => void) => {
      attemptNavigate(action);
    };

    return () => {
      if ((window as any).__wgAccountSettingsGuard) {
        delete (window as any).__wgAccountSettingsGuard;
      }
    };
  }, [attemptNavigate]);

  const handleCancelLeave = () => {
    setConfirmOpen(false);
    setPendingNav(null);
  };

  const handleSaveAndLeave = async () => {
    const ok = await saveGlobalContacts();
    if (!ok) return;
    const next = pendingNav;
    setConfirmOpen(false);
    setPendingNav(null);
    if (next) next();
  };

  // Load global contacts (emails) from API on page open
  useEffect(() => {
    const normalizeEmailItem = (item: any, idx: number) => {
      const papel = item?.role === undefined || item?.role === null ? '' : String(item.role).trim();
      return {
        id: `${item.email || 'email'}-${idx}-${Math.random().toString(36).slice(2, 7)}`,
        email: String(item.email || ''),
        name: String(item.name || ''),
        role: papel,
        sector: '0'
      };
    };

    const loadGlobalContacts = async () => {
      try {
        const res = await httpClient.get('/api/v1/portal/customer/global-address-by-customer');
        const payload = res?.data;
        const ok = payload?.success === true && Number(payload?.status) === 200;
        const dataObj = payload?.data || {};

        if (!ok) {
          const errMsg = Array.isArray(payload?.errors)
            ? payload.errors?.[0]?.errorMessage
            : undefined;
          setSnackbar({
            open: true,
            message: errMsg || 'Falha ao carregar contatos globais.',
            severity: 'error'
          });
          return;
        }

        // New official schema key and fallback for prior draft names
        const emailsList = Array.isArray(dataObj?.addressesesEmailsGeneralToNotifies)
          ? dataObj.addressesesEmailsGeneralToNotifies
          : Array.isArray(dataObj?.addressEmailGeneralToNotify)
            ? dataObj.addressEmailGeneralToNotify
            : [];

        const sectorsList = Array.isArray(dataObj?.sectors)
          ? dataObj.sectors
          : undefined;

        // Telegram configurations alert message
        const tCfg = dataObj?.telegramConfigurations;
        let alertMsg: string | undefined = undefined;
        const statusNum = tCfg?.status === undefined ? undefined : Number(tCfg.status);
        const active = !!tCfg?.active;
        if (active && statusNum === TelegramStatusEnum.ACTIVED_GROUP) {
          alertMsg = 'Grupo de Telegram está preparado.';
        } else if (!tCfg || !active || statusNum === TelegramStatusEnum.PENDING_TO_CREATING_GROUP) {
          alertMsg = 'Não há grupo de Telegram configurado no momento. Caso precise de notificações por Telegram, cadastre os números e entre em contato com o suporte para a ativação.';
        }

        const mappedEmails = emailsList.map(normalizeEmailItem);
        setGlobalContacts((prev) => {
          const next = {
            emails: mappedEmails,
            telegrams: prev.telegrams,
            whatsapps: prev.whatsapps
          };
          initialSnapshotRef.current = makeSnapshot(next);
          setIsDirty(false);
          return next;
        });

        setAllowedSectors(sectorsList);
        setTelegramAlertMessage(alertMsg);
      } catch (err: any) {
        const apiMsg = err?.response?.data?.errors?.[0]?.errorMessage;
        setSnackbar({
          open: true,
          message: apiMsg || 'Falha ao carregar contatos globais.',
          severity: 'error'
        });
      }
    };

    const loadFeatureNotifications = async () => {
      try {
        const res = await httpClient.get('/api/v1/portal/configurations/get-features-immigration-canada-global');
        const payload = res?.data;
        const ok = payload?.success === true && Number(payload?.status) === 200;
        const dataObj = payload?.data || {};

        if (!ok) {
          return;
        }

        const featuresList = Array.isArray(dataObj?.featuresImmigrationCanada) ? dataObj.featuresImmigrationCanada : [];
        const mapped = featuresList.map((f: any) => {
          const ft = Number(f?.featureType ?? 0) as FeatureTypeEnum;
          const ntRaw = f?.notificationEmailType;
          const nt = (ntRaw === undefined || ntRaw === null ? NotificationEmailTypeEnum.USE_GLOBAL_ADDRESS : Number(ntRaw)) as NotificationEmailTypeEnum;

          const customList = Array.isArray(f?.customEmailAddressNotify) ? f.customEmailAddressNotify : [];
          const customEmails = customList
            .filter((x: any) => x && x.email)
            .map((x: any) => ({
              email: String(x.email || ''),
              name: x.name ? String(x.name) : undefined,
              role: x.role ? String(x.role) : undefined
            }));

          return { featureType: ft, notificationEmailType: nt, customEmails };
        }).filter((x: any) => x.featureType && x.featureType !== FeatureTypeEnum.NONE);

        setFeatureNotifications(mapped);
      } catch {
        return;
      }
    };

    loadGlobalContacts();
    loadFeatureNotifications();
  }, []);

  const getFeatureLabel = (featureType: FeatureTypeEnum): string => {
    switch (featureType) {
      case FeatureTypeEnum.CANADA_CA_IRCC_ROUNDS_OF_INVITATIONS:
        return 'Express Entry - Draws';
      case FeatureTypeEnum.CANADA_CA_IRCC_CHECK_PROCESSING_TIMES:
        return 'Processing Times';
      case FeatureTypeEnum.ONTARIO_CA_OINP_INVITATIONS:
        return 'Ontario PNP - Draws';
      case FeatureTypeEnum.MANITOBA_CA_EOI_DRAWS:
        return 'Manitoba PNP - Draws';
      case FeatureTypeEnum.ALBERTA_CA_AAIP_UPDATES:
        return 'Alberta AAIP - Updates';
      case FeatureTypeEnum.COLUMBIA_CA_PNP:
        return 'British Columbia PNP - Draws';
      case FeatureTypeEnum.ALBERTA_CA_AAIP_DRAWS:
        return 'Alberta AAIP - Draws';
      default:
        return `Feature ${featureType}`;
    }
  };

  const formatEmailLine = (item: { email: string; name?: string; role?: string }): string => {
    const email = item.email?.trim() ?? '';
    const name = item.name?.trim();
    const role = item.role?.trim();
    if (!name && !role) return email;
    if (name && !role) return `${name} <${email}>`;
    if (!name && role) return `${role} <${email}>`;
    return `${name} (${role}) <${email}>`;
  };

  return (
    <MainLayout>
      <Box sx={{ flexGrow: 1 }}>
        <Typography variant="h4" component="h1" gutterBottom color="primary" sx={{ fontWeight: 'bold', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <NotificationsIcon />
          {t('notifications_title_main')}
        </Typography>
        <ApiConnectionAlert />
        
        <Typography variant="body1" paragraph sx={{ mb: 4 }}>
          {t('notifications_description_main')}
        </Typography>

        <ContactManager
          title={t('notifications_contact_manager_title')}
          contacts={globalContacts}
          onChange={(next) => {
            setGlobalContacts(next);
            const base = initialSnapshotRef.current;
            if (base) {
              setIsDirty(makeSnapshot(next) !== base);
            }
          }}
          allowedSectorValues={allowedSectors}
          telegramStatusAlertMessage={telegramAlertMessage}
        />

        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={handleSave}
          >
            {t('notifications_btn_save')}
          </Button>
        </Box>

        <Box sx={{ mt: 4 }}>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
            Aplicação por feature
          </Typography>

          {featureNotifications.map((f) => {
            const isGlobal = f.notificationEmailType === NotificationEmailTypeEnum.USE_GLOBAL_ADDRESS;
            const emails = isGlobal ? (globalContacts.emails || []).map(e => ({ email: e.email, name: e.name, role: e.role })) : f.customEmails;
            const label = isGlobal ? 'GLOBAL' : 'CUSTOM';
            const color = isGlobal ? 'primary' : 'default';

            return (
              <Box key={`ft-${f.featureType}`} sx={{ mb: 2, p: 2, border: '1px solid rgba(0,0,0,0.12)', borderRadius: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                    {getFeatureLabel(f.featureType)}
                  </Typography>
                  <Chip size="small" label={label} color={color as any} variant={isGlobal ? 'filled' : 'outlined'} />
                </Box>

                <Box sx={{ mt: 1 }}>
                  {emails.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      Nenhum e-mail configurado.
                    </Typography>
                  ) : (
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {emails.map(formatEmailLine).join('\n')}
                    </Typography>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>

        <Dialog open={confirmOpen} onClose={handleCancelLeave} maxWidth="xs" fullWidth>
          <DialogTitle>Salvar alterações?</DialogTitle>
          <DialogContent>
            <Typography variant="body2">
              Você fez alterações e ainda não salvou. Deseja salvar as informações antes de sair desta página?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelLeave} color="inherit">
              Cancelar
            </Button>
            <Button onClick={handleSaveAndLeave} variant="contained" color="primary">
              Salvar
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </MainLayout>
  );
};

export default Notifications;
